import React, { useRef, useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Separator } from '../ui/separator';
import { Checkbox } from '../ui/checkbox';
import { Combobox } from '../ui/combobox';
import { toast } from 'sonner';
import {
  Package, QrCode, Search, X, Plus, Trash2, Shield, Camera, Upload, History, ChevronRight
} from 'lucide-react';
import SignatureCanvas from 'react-signature-canvas';
import { BinArticleSelector, parseBinQrCode } from '../ui/bin-article-selector';
import { ArticleQuantityDialog } from '../ui/article-quantity-dialog';
import api from '../../lib/api';
import { useQrScanner } from '../../hooks/useQrScanner';

const TicketDialogs = ({
  ticket, ticketId, statusCfg,
  // Reject
  showRejectDialog, setShowRejectDialog, rejectData, setRejectData, onReject,
  // Status change
  showStatusDialog, setShowStatusDialog, statusChangeData, setStatusChangeData, onStatusChange, getAvailableStatuses,
  // Parts ETA (waiting_parts flow)
  showPartsEtaDialog, setShowPartsEtaDialog, partsEtaData, setPartsEtaData, onPartsEtaConfirm, partsEtaMode,
  // Reassign
  showReassignDialog, setShowReassignDialog, reassignData, setReassignData, onReassign, technicians,
  // Transform
  showTransformDialog, setShowTransformDialog, transformData, setTransformData, onTransform, procedures,
  // Close
  showCloseDialog, setShowCloseDialog, closeData, setCloseData, consumedItems, onClose,
  // Consumed items
  pendingArticle, showLotSelector, setShowLotSelector, availableLots, selectedLot, setSelectedLot,
  pendingQty, setPendingQty, lotsLoading, onConfirmAddArticle, onCancelPending,
  // BIN scan
  binScanOpen, setBinScanOpen, binScanCode, binScanType, onBinArticleSelect,
  // QR Scanner
  showQrScanner, onStopQrScanner,
  // Revise
  showReviseDialog, setShowReviseDialog, reviseData, setReviseData,
  reviseConsumedItems, setReviseConsumedItems, reviseClosureImages, setReviseClosureImages,
  onRevise
}) => {
  const techSigRef = useRef(null);
  const opSigRef = useRef(null);
  const closureCameraRef = useRef(null);
  const closureImageRef = useRef(null);
  const [closureImages, setClosureImages] = useState([]);

  // Revise search
  const [reviseArticleSearch, setReviseArticleSearch] = useState('');
  const [reviseSearchResults, setReviseSearchResults] = useState([]);
  const [showReviseQrScanner, setShowReviseQrScanner] = useState(false);
  const { start: startQrHook, stop: stopQrHook } = useQrScanner();

  useEffect(() => {
    if (!showReviseDialog || reviseArticleSearch.length < 2) { setReviseSearchResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const resp = await api.get('/articles', { params: { search: reviseArticleSearch } });
        setReviseSearchResults(resp.data.slice(0, 8));
      } catch { setReviseSearchResults([]); }
    }, 300);
    return () => clearTimeout(t);
  }, [reviseArticleSearch, showReviseDialog]);

  const handleReviseAddArticle = (article) => {
    const existingIdx = reviseConsumedItems.findIndex(item => item.article_id === article.id);
    if (existingIdx >= 0) {
      setReviseConsumedItems(prev => prev.map((item, idx) =>
        idx === existingIdx ? { ...item, quantity: (item.quantity || 1) + 1 } : item
      ));
      toast.success(`${article.name} - cantitate marita`);
    } else {
      setReviseConsumedItems(prev => [...prev, {
        article_id: article.id, article_name: article.name, article_code: article.code,
        quantity: 1, unit: article.unit || 'buc', lot_number: null, supplier_id: null, supplier_name: null, unit_price: 0,
      }]);
      toast.success(`${article.name} adaugat`);
    }
    setReviseArticleSearch('');
    setReviseSearchResults([]);
  };

  const startReviseQrScanner = async () => {
    setShowReviseQrScanner(true);
    setTimeout(async () => {
      try {
        await startQrHook('revise-qr-reader', async (decodedText) => {
          await stopQrHook();
          setShowReviseQrScanner(false);
          const binResult = parseBinQrCode(decodedText);
          if (binResult.isBin) { toast.info('Scanati un cod de articol, nu un cod de bin/raft'); return; }
          let searchCode = decodedText;
          const parts = decodedText.split(':');
          if (parts.length >= 2 && parts[0] === 'ART') searchCode = parts.slice(1).join(':');
          try {
            const resp = await api.get('/articles', { params: { search: searchCode } });
            if (resp.data.length > 0) { handleReviseAddArticle(resp.data[0]); toast.success(`Articol scanat: ${resp.data[0].name}`); }
            else toast.error(`Articol negasit pentru cod: ${searchCode}`);
          } catch { toast.error('Eroare la cautare articol'); }
        }, { fps: 10, qrbox: { width: 250, height: 250 } });
      } catch { toast.error('Nu s-a putut porni camera'); setShowReviseQrScanner(false); }
    }, 300);
  };

  const handleClosureImageSelect = (e) => {
    const files = Array.from(e.target.files || []);
    if (closureImages.length + files.length > 5) { toast.error('Maximum 5 fotografii permise'); return; }
    for (const file of files) {
      if (file.size > 5 * 1024 * 1024) { toast.error(`${file.name}: Fisier prea mare (max 5MB)`); continue; }
      const reader = new FileReader();
      reader.onload = (ev) => {
        const base64Full = ev.target.result;
        setClosureImages(prev => [...prev, { id: Date.now() + Math.random(), preview: base64Full, base64: base64Full.split(',')[1] }]);
      };
      reader.readAsDataURL(file);
    }
    e.target.value = '';
  };

  const handleReviseImageSelect = (e) => {
    const files = Array.from(e.target.files || []);
    if (reviseClosureImages.length + files.length > 5) { toast.error('Maximum 5 fotografii permise'); return; }
    for (const file of files) {
      if (file.size > 5 * 1024 * 1024) { toast.error(`${file.name}: Fisier prea mare (max 5MB)`); continue; }
      const reader = new FileReader();
      reader.onload = (ev) => {
        const base64Full = ev.target.result;
        setReviseClosureImages(prev => [...prev, { id: Date.now() + Math.random(), preview: base64Full, base64: base64Full.split(',')[1] }]);
      };
      reader.readAsDataURL(file);
    }
    e.target.value = '';
  };

  const handleCloseSubmit = () => {
    const techSig = techSigRef.current;
    if (!techSig || techSig.isEmpty()) { toast.error('Semnatura tehnicianului este obligatorie'); return; }
    if (!closeData.operatorSkipped) {
      const opSig = opSigRef.current;
      if (!opSig || opSig.isEmpty()) { toast.error('Semnatura operatorului este obligatorie sau bifati "Operator absent"'); return; }
    }
    onClose({
      technician_signature: techSig.toDataURL('image/png'),
      operator_signature: closeData.operatorSkipped ? null : opSigRef.current?.toDataURL('image/png'),
      operator_signature_skipped: closeData.operatorSkipped,
      operator_skip_reason: closeData.skipReason || null,
      notes: closeData.notes || null,
      consumed_items: consumedItems,
      closure_images: closureImages.map(img => img.base64)
    });
    setClosureImages([]);
  };

  return (
    <>
      <BinArticleSelector open={binScanOpen} binCode={binScanCode} scanType={binScanType} onSelect={onBinArticleSelect} onClose={() => setBinScanOpen(false)} />
      <ArticleQuantityDialog article={pendingArticle && !showLotSelector ? pendingArticle : null} onConfirm={(article, qty) => onConfirmAddArticle(qty)} onCancel={onCancelPending} />

      {/* Lot Selection */}
      <Dialog open={showLotSelector} onOpenChange={(open) => { if (!open) { setShowLotSelector(false); onCancelPending(); } }}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Package className="w-5 h-5 text-blue-500" /> Selecteaza Lot / Furnizor</DialogTitle></DialogHeader>
          {pendingArticle && (
            <div className="space-y-4">
              <div className="bg-blue-50 p-3 rounded-lg">
                <p className="font-bold">{pendingArticle.name}</p>
                <p className="text-sm text-slate-600">{pendingArticle.code}</p>
              </div>
              {lotsLoading ? (
                <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div></div>
              ) : (
                <div className="space-y-2">
                  {availableLots.map((lot, idx) => (
                    <div key={idx} onClick={() => setSelectedLot(lot)}
                      className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${selectedLot?.lot_number === lot.lot_number && selectedLot?.bin_id === lot.bin_id ? 'border-blue-500 bg-blue-50 shadow-sm' : 'border-slate-200 hover:border-blue-300 hover:bg-slate-50'}`}
                      data-testid={`lot-option-${idx}`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="flex items-center gap-2"><Badge variant="outline" className="text-xs font-mono">{lot.lot_number}</Badge><span className="text-sm font-semibold">{lot.supplier_name}</span></div>
                          <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                            <span>BIN: {lot.bin_code}</span>
                            {lot.entry_date && <span>{new Date(lot.entry_date).toLocaleDateString('ro-RO')}</span>}
                            {lot.unit_price > 0 && <span className="font-mono">{lot.unit_price.toFixed(2)} RON/buc</span>}
                          </div>
                        </div>
                        <div className="text-right"><p className="font-bold text-lg">{lot.quantity}</p><p className="text-xs text-slate-500">{pendingArticle.unit || 'buc'}</p></div>
                      </div>
                    </div>
                  ))}
                  {availableLots.length === 0 && <div className="text-center py-6 text-slate-500"><Package className="w-8 h-8 mx-auto text-slate-300 mb-2" /><p>Niciun stoc disponibil</p></div>}
                </div>
              )}
              {selectedLot && (
                <div className="flex items-center gap-3 pt-2 border-t">
                  <div className="flex-1">
                    <Label className="text-xs text-slate-500">Cantitate</Label>
                    <Input type="number" min="1" max={selectedLot.quantity} value={pendingQty} onChange={(e) => setPendingQty(Math.min(parseInt(e.target.value) || 1, selectedLot.quantity))} className="h-10" data-testid="lot-qty-input" />
                  </div>
                  <Button className="h-10 mt-5" onClick={() => onConfirmAddArticle(pendingQty)} data-testid="confirm-lot-selection"><Plus className="w-4 h-4 mr-1" /> Adauga</Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>Respinge Tichet</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label>Motivul respingerii *</Label><Textarea value={rejectData.rejection_reason} onChange={(e) => setRejectData({ ...rejectData, rejection_reason: e.target.value })} rows={4} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowRejectDialog(false)}>Anuleaza</Button><Button variant="destructive" onClick={onReject}>RESPINGE</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Status Change */}
      <Dialog open={showStatusDialog} onOpenChange={setShowStatusDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>Schimba Status</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="bg-slate-50 p-3 rounded-lg"><Badge className={statusCfg.color}>{statusCfg.label}</Badge><ChevronRight className="w-4 h-4 inline mx-2 text-slate-400" /><span className="text-slate-500">?</span></div>
            <div className="space-y-2"><Label>Noul status *</Label>
              <Select value={statusChangeData.status} onValueChange={(v) => setStatusChangeData({ ...statusChangeData, status: v })}>
                <SelectTrigger className="h-11"><SelectValue placeholder="Selecteaza status" /></SelectTrigger>
                <SelectContent>{getAvailableStatuses(ticket.status).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2"><Label>Note</Label><Textarea value={statusChangeData.notes} onChange={(e) => setStatusChangeData({ ...statusChangeData, notes: e.target.value })} rows={3} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowStatusDialog(false)}>Anuleaza</Button><Button className="btn-primary" onClick={onStatusChange}>ACTUALIZEAZA</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Parts ETA Dialog (Waiting Parts) */}
      <Dialog open={showPartsEtaDialog} onOpenChange={setShowPartsEtaDialog}>
        <DialogContent data-testid="parts-eta-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="w-5 h-5 text-orange-500" />
              {partsEtaMode === 'edit' ? 'Modifica Termen Piese' : 'Asteapta Piese - Termen Livrare'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-3 rounded-lg bg-orange-50 border border-orange-200 text-sm text-orange-800">
              {partsEtaMode === 'edit'
                ? 'Actualizati termenul estimativ in care vor ajunge piesele comandate.'
                : 'Introduceti termenul estimativ (in zile) pana la sosirea pieselor comandate de la furnizor. Aceasta informatie va fi vizibila peste tot pentru a reduce intrebarile despre status.'}
            </div>
            <div className="space-y-2">
              <Label className="font-semibold">Numar zile estimate *</Label>
              <Input
                type="number"
                min="1"
                max="365"
                value={partsEtaData?.days || ''}
                onChange={(e) => setPartsEtaData({ ...partsEtaData, days: e.target.value })}
                placeholder="ex: 20"
                className="h-11 text-lg"
                data-testid="parts-eta-days-input"
                autoFocus
              />
              {partsEtaData?.days && Number(partsEtaData.days) > 0 && (
                <p className="text-xs text-slate-500">
                  Piesele vor ajunge estimativ pe: <strong>{new Date(Date.now() + Number(partsEtaData.days) * 86400000).toLocaleDateString('ro-RO', { day: '2-digit', month: 'long', year: 'numeric' })}</strong>
                </p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPartsEtaDialog(false)} data-testid="parts-eta-cancel-btn">Anuleaza</Button>
            <Button
              className="bg-orange-600 hover:bg-orange-700 text-white disabled:opacity-50"
              onClick={onPartsEtaConfirm}
              disabled={(() => { const n = Number(partsEtaData?.days); return !n || n <= 0 || n > 365; })()}
              data-testid="parts-eta-confirm-btn"
            >
              {partsEtaMode === 'edit' ? 'SALVEAZA TERMEN' : 'CONFIRMA & SCHIMBA STATUS'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reassign */}
      <Dialog open={showReassignDialog} onOpenChange={setShowReassignDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>Reasigneaza Tichet</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label>Noul tehnician *</Label>
              <Select value={reassignData.new_assignee_id} onValueChange={(v) => setReassignData({ ...reassignData, new_assignee_id: v })}>
                <SelectTrigger className="h-11"><SelectValue placeholder="Selecteaza tehnician" /></SelectTrigger>
                <SelectContent>{technicians.filter(u => u.id !== ticket.assigned_to).map((u) => <SelectItem key={u.id} value={u.id}>{u.full_name} ({u.role})</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2"><Label>Motiv</Label><Textarea value={reassignData.notes} onChange={(e) => setReassignData({ ...reassignData, notes: e.target.value })} rows={3} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowReassignDialog(false)}>Anuleaza</Button><Button className="btn-primary" onClick={onReassign}>REASIGNEAZA</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Transform */}
      <Dialog open={showTransformDialog} onOpenChange={setShowTransformDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>Transforma in Revizie</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label>Data planificata *</Label><Input type="date" value={transformData.scheduled_date} onChange={(e) => setTransformData({ ...transformData, scheduled_date: e.target.value })} className="h-11" /></div>
            <div className="space-y-2"><Label>Procedura</Label>
              <Combobox items={procedures} value={transformData.procedure_id} onSelect={(id) => setTransformData({ ...transformData, procedure_id: id })} placeholder="Selecteaza procedura..." searchPlaceholder="Cauta procedura..." emptyText="Niciun procedura gasita." nameKey="name" descKey="descriere" codeKey="cod" />
            </div>
            <div className="space-y-2"><Label>Executor</Label>
              <Combobox items={technicians.map(u => ({ ...u, name: u.full_name, description: u.role }))} value={transformData.assigned_to} onSelect={(id) => setTransformData({ ...transformData, assigned_to: id })} placeholder="Selecteaza tehnician..." searchPlaceholder="Cauta tehnician..." emptyText="Niciun tehnician gasit." />
            </div>
            <div className="space-y-2"><Label>Ore estimate</Label><Input type="number" value={transformData.estimated_hours} onChange={(e) => setTransformData({ ...transformData, estimated_hours: e.target.value })} className="h-11" min="0" step="0.5" /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowTransformDialog(false)}>Anuleaza</Button><Button className="btn-primary" onClick={onTransform}>TRANSFORMA</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Close with Signature */}
      <Dialog open={showCloseDialog} onOpenChange={setShowCloseDialog}>
        <DialogContent className="max-w-2xl max-h-[95vh] overflow-y-auto w-[95vw] sm:w-auto">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Shield className="w-5 h-5" /> Inchidere Tichet cu Semnatura</DialogTitle></DialogHeader>
          <div className="space-y-6">
            <div className="space-y-2" data-testid="technician-signature-section">
              <Label className="font-semibold">Semnatura Tehnician *</Label>
              <div className="border-2 border-slate-300 rounded-lg bg-white relative" style={{ height: '160px' }}>
                <SignatureCanvas ref={techSigRef} penColor="black" minWidth={1.5} maxWidth={3}
                  canvasProps={{ width: 500, height: 156, className: 'rounded-lg', style: { width: '100%', height: '100%', borderRadius: '0.5rem' } }}
                  backgroundColor="rgb(255,255,255)" />
                <div className="absolute bottom-2 left-0 right-0 border-t border-dashed border-slate-300 mx-6"></div>
                <span className="absolute bottom-1 right-3 text-[10px] text-slate-400">Semnati aici</span>
              </div>
              <Button variant="ghost" size="sm" onClick={() => techSigRef.current?.clear()}><X className="w-3 h-3 mr-1" /> Sterge semnatura</Button>
            </div>
            <Separator />
            <div className="space-y-3" data-testid="operator-signature-section">
              <Label className="font-semibold">Semnatura Operator</Label>
              <div className="flex items-center gap-3 p-3 rounded-lg bg-amber-50 border border-amber-200">
                <Checkbox checked={closeData.operatorSkipped} onCheckedChange={(checked) => setCloseData({ ...closeData, operatorSkipped: checked })} id="skip-operator" data-testid="skip-operator-checkbox" />
                <label htmlFor="skip-operator" className="text-sm cursor-pointer">Operatorul nu este prezent sau refuza semnarea</label>
              </div>
              {closeData.operatorSkipped ? (
                <Select value={closeData.skipReason} onValueChange={(v) => setCloseData({ ...closeData, skipReason: v })}>
                  <SelectTrigger className="h-11"><SelectValue placeholder="Selecteaza motivul" /></SelectTrigger>
                  <SelectContent><SelectItem value="not_present">Operator absent</SelectItem><SelectItem value="refused">Operator refuza semnarea</SelectItem></SelectContent>
                </Select>
              ) : (
                <>
                  <div className="border-2 border-slate-300 rounded-lg bg-white relative" style={{ height: '160px' }}>
                    <SignatureCanvas ref={opSigRef} penColor="black" minWidth={1.5} maxWidth={3}
                      canvasProps={{ width: 500, height: 156, className: 'rounded-lg', style: { width: '100%', height: '100%', borderRadius: '0.5rem' } }}
                      backgroundColor="rgb(255,255,255)" />
                    <div className="absolute bottom-2 left-0 right-0 border-t border-dashed border-slate-300 mx-6"></div>
                    <span className="absolute bottom-1 right-3 text-[10px] text-slate-400">Semnati aici</span>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => opSigRef.current?.clear()}><X className="w-3 h-3 mr-1" /> Sterge semnatura</Button>
                </>
              )}
            </div>
            <Separator />
            <div className="space-y-2" data-testid="closure-photos-section">
              <Label className="font-semibold flex items-center gap-2"><Camera className="w-4 h-4 text-green-500" /> Fotografii Dovada (optional, max 5)</Label>
              {closureImages.length > 0 && (
                <div className="flex gap-2 flex-wrap">
                  {closureImages.map(img => (
                    <div key={img.id} className="relative w-20 h-20 rounded-lg overflow-hidden border">
                      <img src={img.preview} alt="" className="w-full h-full object-cover" />
                      <button type="button" onClick={() => setClosureImages(prev => prev.filter(i => i.id !== img.id))} className="absolute top-0.5 right-0.5 w-5 h-5 bg-red-600 rounded-full flex items-center justify-center"><X className="w-3 h-3 text-white" /></button>
                    </div>
                  ))}
                </div>
              )}
              {closureImages.length < 5 && (
                <div className="flex gap-2">
                  <input ref={closureCameraRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={handleClosureImageSelect} />
                  <input ref={closureImageRef} type="file" accept="image/*" multiple className="hidden" onChange={handleClosureImageSelect} />
                  <Button type="button" variant="outline" size="sm" className="flex-1 h-10 gap-1.5" onClick={() => closureCameraRef.current?.click()} data-testid="closure-camera-btn"><Camera className="w-4 h-4" /> Fotografiaza</Button>
                  <Button type="button" variant="outline" size="sm" className="flex-1 h-10 gap-1.5" onClick={() => closureImageRef.current?.click()} data-testid="closure-upload-btn"><Upload className="w-4 h-4" /> Incarca</Button>
                </div>
              )}
            </div>
            <Separator />
            <div className="space-y-2"><Label>Note de inchidere</Label><Textarea value={closeData.notes} onChange={(e) => setCloseData({ ...closeData, notes: e.target.value })} placeholder="Note optionale despre rezolvare..." rows={3} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowCloseDialog(false)}>Anuleaza</Button><Button className="bg-slate-700 hover:bg-slate-800" onClick={handleCloseSubmit} data-testid="confirm-close-btn"><Shield className="w-4 h-4 mr-2" /> INCHIDE TICHETUL</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* QR Scanner */}
      <Dialog open={showQrScanner} onOpenChange={(open) => { if (!open) onStopQrScanner(); }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><QrCode className="w-5 h-5" /> Scaneaza QR Cod Articol</DialogTitle></DialogHeader>
          <div className="space-y-4"><div id="ticket-qr-reader" className="w-full rounded-lg overflow-hidden" style={{ minHeight: '300px' }}></div><p className="text-sm text-center text-slate-500">Indreptati camera spre codul QR al articolului</p></div>
          <DialogFooter><Button variant="outline" onClick={onStopQrScanner}>Inchide</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Revision Dialog */}
      <Dialog open={showReviseDialog} onOpenChange={setShowReviseDialog}>
        <DialogContent className="max-w-2xl max-h-[95vh] overflow-y-auto w-[95vw] sm:w-auto" data-testid="revise-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><History className="w-5 h-5 text-amber-500" /> Revizuire Tichet</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-800">Toate modificarile sunt pastrate in istoric pentru audit. Campurile nemodificate raman la valorile anterioare.</div>
            <div className="space-y-2"><Label className="font-semibold text-red-600">Motiv revizuire *</Label><Textarea value={reviseData.reason} onChange={(e) => setReviseData({ ...reviseData, reason: e.target.value })} placeholder="De ce este necesara revizuirea?" rows={2} className="border-amber-300 focus:border-amber-400" data-testid="revise-reason-input" /></div>
            <div className="space-y-2"><Label>Note suplimentare</Label><Textarea value={reviseData.notes} onChange={(e) => setReviseData({ ...reviseData, notes: e.target.value })} placeholder="Note optionale..." rows={2} data-testid="revise-notes-input" /></div>
            <Separator />
            <div className="space-y-3">
              <Label className="font-semibold flex items-center gap-2"><Package className="w-4 h-4 text-blue-500" /> Materiale Consumate</Label>
              <div className="flex gap-2">
                <div className="relative flex-1"><Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" /><Input value={reviseArticleSearch} onChange={(e) => setReviseArticleSearch(e.target.value)} placeholder="Cauta articol pentru a adauga..." className="pl-10 h-10" data-testid="revise-article-search" /></div>
                <Button type="button" variant="outline" size="icon" className="h-10 w-10 shrink-0" onClick={startReviseQrScanner} data-testid="revise-scan-qr-btn"><QrCode className="w-4 h-4" /></Button>
              </div>
              {reviseSearchResults.length > 0 && (
                <div className="border rounded-lg divide-y bg-white max-h-40 overflow-y-auto shadow-sm">
                  {reviseSearchResults.map(article => (
                    <div key={article.id} onClick={() => handleReviseAddArticle(article)} className="p-2.5 hover:bg-blue-50 cursor-pointer flex items-center justify-between transition-colors" data-testid={`revise-article-result-${article.id}`}>
                      <div><p className="font-medium text-sm">{article.name}</p><div className="flex items-center gap-2 text-xs text-slate-500"><span>Cod: {article.code}</span><span className={article.total_stock > 0 ? 'text-green-600 font-semibold' : 'text-red-600 font-semibold'}>Stoc: {article.total_stock || 0}</span></div></div>
                      <Plus className="w-5 h-5 text-blue-600" />
                    </div>
                  ))}
                </div>
              )}
              {reviseConsumedItems.length > 0 ? (
                <div className="space-y-2">
                  {reviseConsumedItems.map((item, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 rounded-lg border bg-slate-50 gap-2">
                      <div className="flex-1 min-w-0"><p className="text-sm font-medium truncate">{item.article_name}</p><p className="text-xs text-slate-500">{item.article_code}</p></div>
                      <div className="flex items-center gap-1">
                        <button className="w-7 h-7 rounded border bg-white flex items-center justify-center text-sm font-bold" onClick={() => setReviseConsumedItems(prev => prev.map((it, i) => i === idx ? { ...it, quantity: Math.max(0.5, (it.quantity || 1) - 1) } : it))} data-testid={`revise-qty-minus-${idx}`}>-</button>
                        <span className="w-10 text-center text-sm font-semibold">{item.quantity}</span>
                        <button className="w-7 h-7 rounded border bg-white flex items-center justify-center text-sm font-bold" onClick={() => setReviseConsumedItems(prev => prev.map((it, i) => i === idx ? { ...it, quantity: (it.quantity || 0) + 1 } : it))} data-testid={`revise-qty-plus-${idx}`}>+</button>
                        <span className="text-xs text-slate-500 w-8 ml-1">{item.unit}</span>
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => setReviseConsumedItems(prev => prev.filter((_, i) => i !== idx))} data-testid={`revise-remove-${idx}`}><Trash2 className="w-3.5 h-3.5 text-red-500" /></Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-4 border rounded-lg border-dashed border-slate-300"><Package className="w-6 h-6 mx-auto text-slate-300 mb-1" /><p className="text-sm text-slate-400">Niciun material consumat. Cautati pentru a adauga.</p></div>
              )}
            </div>
            <Separator />
            <div className="space-y-2">
              <Label className="font-semibold flex items-center gap-2"><Camera className="w-4 h-4 text-green-500" /> Fotografii Dovada</Label>
              {reviseClosureImages.length > 0 && (
                <div className="flex gap-2 flex-wrap">
                  {reviseClosureImages.map(img => (
                    <div key={img.id} className="relative w-20 h-20 rounded-lg overflow-hidden border">
                      <img src={img.path ? `${process.env.REACT_APP_BACKEND_URL}${img.path}` : img.preview} alt="" className="w-full h-full object-cover" />
                      <button type="button" onClick={() => setReviseClosureImages(prev => prev.filter(i => i.id !== img.id))} className="absolute top-0.5 right-0.5 w-5 h-5 bg-red-600 rounded-full flex items-center justify-center"><X className="w-3 h-3 text-white" /></button>
                    </div>
                  ))}
                </div>
              )}
              {reviseClosureImages.length < 5 && (
                <div className="flex gap-2">
                  <input type="file" accept="image/*" multiple className="hidden" onChange={handleReviseImageSelect} id="revise-img-input" />
                  <Button type="button" variant="outline" size="sm" className="flex-1 h-10 gap-1.5" onClick={() => document.getElementById('revise-img-input')?.click()} data-testid="revise-upload-btn"><Upload className="w-4 h-4" /> Adauga Fotografii</Button>
                </div>
              )}
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowReviseDialog(false)}>Anuleaza</Button><Button className="bg-amber-600 hover:bg-amber-700 text-white" onClick={onRevise} disabled={!reviseData.reason.trim()} data-testid="confirm-revise-btn"><History className="w-4 h-4 mr-2" /> SALVEAZA REVIZUIREA</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* QR for Revision */}
      <Dialog open={showReviseQrScanner} onOpenChange={(open) => { if (!open) { stopQrHook(); setShowReviseQrScanner(false); } }}>
        <DialogContent className="max-w-md" data-testid="revise-qr-scanner-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><QrCode className="w-5 h-5" /> Scaneaza QR Articol (Revizie)</DialogTitle></DialogHeader>
          <div id="revise-qr-reader" className="w-full aspect-square bg-slate-100 rounded-lg overflow-hidden"></div>
          <p className="text-xs text-slate-500 text-center">Pozitionati codul QR al articolului in cadru</p>
          <DialogFooter><Button variant="outline" onClick={() => { stopQrHook(); setShowReviseQrScanner(false); }}>Anuleaza</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default TicketDialogs;
